import { conciseOpeningName, ecoName, resultColor } from "@/shared/utils/chess";
import { formatDate } from "@/shared/utils/format";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  forwardRef,
  startTransition,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  LinearProgress,
  Skeleton,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { motion, useAnimationControls, useReducedMotion } from "motion/react";
import type { GameListFilters, GameListItem } from "./games.api";
import { useGames } from "./useGames";
import {
  useGamesStore,
  isGamesFlow,
  type GameCategory,
  type GameSort,
  type GameSortKey,
  type GamesFlowState,
} from "./games.store";
import { useRememberedPageState, useRememberedScroll } from "@/features/preferences/page-state";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import { useAuth } from "@/app/context/AuthContext";
import { layoutTokens } from "@/shared/theme";
import { useBreakpointDown } from "@/shared/hooks";
import { gameReviewPath, gamesOrigin } from "./review-navigation";
import { SingleGameAnalysisModal, type CompletedSingleGameAnalysis } from "../analysis/SingleGameAnalysisModal";
import {
  createRevealAnimationCoordinator,
  requestCoordinatedFinalReveal,
  type RevealAnimationCoordinator,
} from "@/shared/components/ScrollCardReveal";
import {
  GAME_CATEGORIES,
  GAME_PREFETCH_REMAINING_ROWS,
  GAME_ROW_HEIGHT,
  GAME_ROW_TRANSFORM_GUTTER,
  TABLE_BODY_HEIGHT,
  TABLE_ROW_REVEAL_DELAY_MS,
  VISIBLE_GAME_ROWS,
} from "./constants";
import { FormatPicker, SelectionClockCard, SelectionFlow, SelectionStepIntro } from "@/shared/components";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveAnalysisAction, type ActionEntitlement } from "@/features/entitlements/entitlement-policy";
import {
  GAME_TABLE_ACTION_MIN_WIDTH,
  GAME_TABLE_ACTION_SX,
  GAME_TABLE_COLUMN_WIDTHS as COL_WIDTHS,
  gameFormatEvidence,
  gameInitialRowRevealDelay,
  gameRowRevealObserverOptions,
} from "./game-table-presentation";

interface GamesAnalysisModalHandle {
  open: (gameId: string) => void;
}

type MarkGameAnalyzed = (
  gameId: string,
  result: CompletedSingleGameAnalysis["result"] & { analysisDepth: number },
) => void;

type NavigateToReview = (to: string, options: { state: { gamesOrigin: string } }) => unknown;

export const finishGameAnalysis = (
  completion: CompletedSingleGameAnalysis,
  gamesReturnUrl: string,
  markGameAnalyzed: MarkGameAnalyzed,
  navigate: NavigateToReview,
) => {
  markGameAnalyzed(completion.gameId, {
    ...completion.result,
    analysisDepth: completion.analysisDepth,
  });
  void navigate(gameReviewPath(completion.gameId, "review"), {
    state: { gamesOrigin: gamesReturnUrl },
  });
};

const GamesAnalysisModal = forwardRef<
  GamesAnalysisModalHandle,
  {
    onComplete: (completion: CompletedSingleGameAnalysis) => void;
  }
>(function GamesAnalysisModal({ onComplete }, ref) {
  const [gameId, setGameId] = useState<string | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      open: setGameId,
    }),
    [],
  );

  return <SingleGameAnalysisModal gameId={gameId} onClose={() => setGameId(null)} onComplete={onComplete} />;
});

const headerSx = {
  height: { xs: 56, sm: 48 },
  boxSizing: "border-box",
  fontWeight: 700,
  color: "text.secondary",
  fontSize: { xs: "0.69rem", lg: "0.74rem" },
  letterSpacing: "0.06em",
  py: 0,
  px: { xs: 0.5, lg: 0.75 },
  bgcolor: "background.paper",
  borderTop: "1px solid rgba(255,255,255,0.15) !important",
  borderBottom: "1px solid rgba(255,255,255,0.15) !important",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const bodySx = {
  py: 0.6,
  px: 0.75,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "0.75rem",
};
const hiddenOnMobileSx = { display: { xs: "none", sm: "table-cell" } };
const wideDesktopSx = { display: { xs: "none", lg: "table-cell" } };
const formatCompactGameDate = (date: string) => {
  const formatted = formatDate(date);
  return `${formatted.slice(0, 6)}${formatted.slice(-2)}`;
};

const ratingChangeColor = (change: number | null) => {
  if (change == null || change === 0) return "#6b7280";
  return change > 0 ? "#22c55e" : "#ef4444";
};

const ratingChangePrefix = (change: number | null) => {
  if (change == null) return "•";
  if (change === 0) return "±0";
  return change > 0 ? `+${change}` : String(change);
};

interface TimeControlTableProps {
  label: string;
  games: GameListItem[];
  onAnalyze: (game: GameListItem) => void;
  analysisAction: (game: GameListItem) => Omit<ActionEntitlement, "estimate">;
  sort: GameSort | null;
  onSort: (key: GameSortKey) => void;
  onLoadMore: () => void;
  hasMoreGames: boolean;
  isLoadingMore: boolean;
  isVisuallyMounted: boolean;
  animateRows: boolean;
  isSearchFiltering: boolean;
}

const durationLabel = (seconds: number) => {
  if (seconds >= 86_400 && seconds % 86_400 === 0) return `${seconds / 86_400} day`;
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} sec`;
};

const durationUnitLabel = (seconds: number) => {
  if (seconds >= 86_400 && seconds % 86_400 === 0) {
    return seconds / 86_400 === 1 ? "DAY" : "DAYS";
  }
  if (seconds >= 60 && seconds % 60 === 0) {
    return seconds / 60 === 1 ? "MINUTE" : "MINUTES";
  }
  return seconds === 1 ? "SECOND" : "SECONDS";
};

const parseClockSignature = (signature: string) => {
  if (signature === "unknown") return null;
  // eslint-disable-next-line security/detect-unsafe-regex -- Both numeric fields are bounded and separated by a literal plus.
  const match = signature.match(/^(\d{1,10})(?:\+(\d{1,10}))?$/);
  if (match) {
    return { base: Number(match[1]), increment: Number(match[2] ?? 0) };
  }

  // Chess.com encodes daily controls as days per move over 86400 seconds.
  const dailyMatch = signature.match(/^(\d+)\/(\d+)$/);
  if (!dailyMatch) return null;
  return {
    base: Number(dailyMatch[1]) * Number(dailyMatch[2]),
    increment: 0,
  };
};

const clockGroupKey = (signature: string | null) =>
  signature != null && parseClockSignature(signature) ? signature : "unknown";

const clockSignatureLabel = (signature: string) => {
  const clock = parseClockSignature(signature);
  if (!clock) return "Unspecified clock";
  return clock.increment > 0 ? `${durationLabel(clock.base)} + ${clock.increment} sec` : durationUnitLabel(clock.base);
};

const tableClockLabel = (signature: string | null) => {
  const clock = signature ? parseClockSignature(signature) : null;
  if (!clock) return "CLOCK UNKNOWN";
  const base = durationLabel(clock.base).toUpperCase();
  return clock.increment > 0 ? `${base} + ${clock.increment} SEC` : base;
};

const clockSignatureGlyph = (signature: string) => {
  const clock = parseClockSignature(signature);
  if (!clock) return "?";
  const base =
    clock.base >= 86_400 && clock.base % 86_400 === 0
      ? Math.round(clock.base / 86_400)
      : clock.base >= 60 && clock.base % 60 === 0
        ? Math.round(clock.base / 60)
        : clock.base;
  return clock.increment > 0 ? `${base} | ${clock.increment}` : String(base);
};

export const SelectionLoadingSurface = ({
  label,
  progress,
}: {
  label: string;
  progress: { loaded: number; total: number } | null;
}) => {
  const [displayedLoaded, setDisplayedLoaded] = useState(0);
  const displayedLoadedRef = useRef(0);
  const lastProgressAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!progress) {
      displayedLoadedRef.current = 0;
      setDisplayedLoaded(0);
      return;
    }
    const start = displayedLoadedRef.current;
    const target = progress.loaded;
    const startedAt = performance.now();
    const previousProgressAt = lastProgressAtRef.current ?? startedAt;
    const updateGap = startedAt - previousProgressAt;
    const duration = Math.min(700, Math.max(220, updateGap * 5));
    lastProgressAtRef.current = startedAt;
    let frameId: number;
    const tick = (now: number) => {
      const elapsed = Math.min(1, (now - startedAt) / duration);
      const next = Math.round(start + (target - start) * (1 - (1 - elapsed) ** 3));
      displayedLoadedRef.current = next;
      setDisplayedLoaded(next);
      if (elapsed < 1) frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [progress]);

  if (!progress) return null;

  const percentage = progress.total === 0 ? 100 : Math.min(100, (progress.loaded / progress.total) * 100);

  return (
    <Box
      aria-live="polite"
      sx={{
        overflow: "hidden",
        position: "relative",
        border: "1px solid",
        borderColor: "primary.main",
        bgcolor: "background.paper",
        minHeight: 88,
        p: 2,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          width: `${percentage}%`,
          bgcolor: "primary.main",
          transition: "width 420ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          mixBlendMode: "difference",
          color: "#f0ece2",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "inherit",
            display: "block",
            fontWeight: 800,
            letterSpacing: "0.1em",
          }}
        >
          LOADING {label.toUpperCase()}
        </Typography>
        <Typography
          sx={{
            color: "inherit",
            fontSize: "1.5rem",
            fontWeight: 800,
            fontVariantNumeric: "tabular-nums",
            mt: 0.25,
          }}
        >
          {displayedLoaded} / {progress.total}
        </Typography>
        <Typography variant="caption" sx={{ color: "inherit" }}>
          games ready for review
        </Typography>
      </Box>
    </Box>
  );
};

const DebouncedGameSearch = ({
  value,
  onChange,
  onFilteringChange,
}: {
  value: string;
  onChange: (value: string) => void;
  onFilteringChange: (isFiltering: boolean) => void;
}) => {
  const [draft, setDraft] = useState(value);
  const submittedValue = useRef(value);

  useEffect(() => {
    if (value === submittedValue.current) return;
    submittedValue.current = value;
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) {
      onFilteringChange(false);
      return;
    }
    const timer = window.setTimeout(() => {
      submittedValue.current = draft;
      startTransition(() => {
        onChange(draft);
        onFilteringChange(false);
      });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [draft, onChange, onFilteringChange, value]);

  return (
    <TextField
      size="small"
      placeholder="Search games"
      inputProps={{ "aria-label": "Search games by opponent or opening" }}
      value={draft}
      onChange={(event) => {
        const nextDraft = event.target.value;
        setDraft(nextDraft);
        if (nextDraft !== value) onFilteringChange(true);
      }}
      sx={{
        display: "block",
        flexShrink: 0,
        width: { xs: "100%", md: 420 },
        "& .MuiOutlinedInput-root": {
          borderRadius: 0,
          fontSize: "0.8rem",
          minHeight: 52,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
          },
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          </InputAdornment>
        ),
      }}
    />
  );
};

const sortValue = (game: GameListItem, key: GameSortKey): string | number => {
  switch (key) {
    case "result":
      return game.result;
    case "opponent":
      return game.opponent.toLowerCase();
    case "rating":
      return game.playerRating;
    case "date":
      return new Date(game.playedAt).getTime();
    case "opening":
      return (game.openingName ?? game.eco ?? "").toLowerCase();
    case "accuracy":
      return game.accuracy ?? game.engineAccuracy ?? -1;
  }
};

const SortableHeader = ({
  label,
  sort,
  sortKey,
  onSort,
  align = "left",
  hiddenOnMobile = false,
  mobileLabel,
}: {
  label: string;
  sort: GameSort | null;
  sortKey: GameSortKey;
  onSort: (key: GameSortKey) => void;
  align?: "left" | "center" | "right";
  hiddenOnMobile?: boolean;
  mobileLabel?: string;
}) => (
  <TableCell sx={{ ...headerSx, ...(hiddenOnMobile ? hiddenOnMobileSx : {}), textAlign: align }}>
    <TableSortLabel
      hideSortIcon
      active={sort?.key === sortKey}
      direction={sort?.key === sortKey ? sort.direction : "asc"}
      onClick={() => onSort(sortKey)}
      sx={{
        color: "text.secondary",
        fontSize: "inherit",
        "&.Mui-active": { color: "text.primary" },
        "& .MuiTableSortLabel-icon": {
          color: "inherit !important",
          fontSize: "0.8rem",
        },
      }}
    >
      {mobileLabel ? (
        <>
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            {label}
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            {mobileLabel}
          </Box>
        </>
      ) : (
        label
      )}
    </TableSortLabel>
  </TableCell>
);

const AccuracyCell = ({ game }: { game: GameListItem }) => {
  const accuracy = game.accuracy ?? game.engineAccuracy;

  return (
    <TableCell sx={{ ...bodySx, textAlign: "center" }}>
      {accuracy != null ? (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: accuracy >= 75 ? "primary.main" : "text.secondary",
          }}
        >
          {accuracy.toFixed(0)}%
        </Typography>
      ) : (
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.15)" }}>
          —
        </Typography>
      )}
    </TableCell>
  );
};

const MotionTableRow = motion.create(TableRow);

const RevealingGameRow = ({
  children,
  index,
  revealKey,
  revealedKeys,
  scrollRoot,
  animationCoordinator,
  isVisuallyMounted,
  animateRows,
  onClick,
  onKeyDown,
  tabIndex,
  ariaLabel,
}: {
  children: ReactNode;
  index: number;
  revealKey: string;
  revealedKeys: Set<string>;
  scrollRoot: HTMLDivElement | null;
  animationCoordinator: RevealAnimationCoordinator;
  isVisuallyMounted: boolean;
  animateRows: boolean;
  onClick?: (event: React.MouseEvent<HTMLTableRowElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTableRowElement>) => void;
  tabIndex?: number;
  ariaLabel?: string;
}) => {
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  const [revealed, setRevealed] = useState(() => revealedKeys.has(revealKey));
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const animationControls = useAnimationControls();
  const animationGeneration = useRef(0);
  const touchStartY = useRef<number | null>(null);
  const ignoreNextClick = useRef(false);
  const prefersReducedMotion = useReducedMotion();
  const renderWithoutAnimation = isVisuallyMounted && !animateRows;

  useLayoutEffect(() => {
    const row = rowRef.current;
    const root = scrollRoot;
    if (!row || revealedKeys.has(revealKey)) {
      setRevealed(true);
      return;
    }
    if (!isVisuallyMounted || !animateRows) return;

    const reveal = (coordinated: boolean) => {
      if (revealedKeys.has(revealKey)) return;
      revealedKeys.add(revealKey);
      setShouldAnimate(true);
      setRevealed(true);
      if (!coordinated) return;

      const previousIndex = animationCoordinator.recentKeys.indexOf(revealKey);
      if (previousIndex >= 0) {
        animationCoordinator.recentKeys.splice(previousIndex, 1);
      }
      animationCoordinator.recentKeys.push(revealKey);

      const isActivelyScrolling = performance.now() - animationCoordinator.lastScrollAt < 120;
      const velocity = isActivelyScrolling ? animationCoordinator.scrollVelocity : 0;
      const speedFactor = Math.min(1, Math.max(0, (velocity - 0.3) / 2.7));
      const newestDuration = 0.24 - 0.09 * speedFactor;
      const trailingDuration = 0.14 - 0.065 * speedFactor;
      const finishGap = 0.012 - 0.008 * speedFactor;
      const durationsByAge = [newestDuration, trailingDuration, trailingDuration - finishGap];
      const bouncesByAge = [0.28, 0.1, 0.04];
      animationCoordinator.recentKeys.forEach((key, recentIndex, keys) => {
        const age = keys.length - 1 - recentIndex;
        const duration = durationsByAge[age] ?? 0.045;
        const bounce = bouncesByAge[age] ?? 0;
        animationCoordinator.durations.set(key, duration);
        animationCoordinator.bounces.set(key, bounce);
        const runner = animationCoordinator.runners.get(key);
        if (runner) void runner(duration, bounce);
      });

      while (animationCoordinator.recentKeys.length > 3) {
        const staleKey = animationCoordinator.recentKeys.shift();
        if (staleKey) {
          animationCoordinator.durations.delete(staleKey);
          animationCoordinator.bounces.delete(staleKey);
        }
      }
    };

    if (!root) {
      const timer = window.setTimeout(() => reveal(false), gameInitialRowRevealDelay(index));
      return () => window.clearTimeout(timer);
    }

    const rowRect = row.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    const initiallyVisible = rowRect.bottom > rootRect.top && rowRect.top < rootRect.bottom;
    if (initiallyVisible && index < VISIBLE_GAME_ROWS) {
      const timer = window.setTimeout(() => reveal(false), gameInitialRowRevealDelay(index));
      return () => window.clearTimeout(timer);
    }
    if (initiallyVisible) {
      const timer = window.setTimeout(() => reveal(true), TABLE_ROW_REVEAL_DELAY_MS);
      return () => window.clearTimeout(timer);
    }

    if (typeof IntersectionObserver === "undefined") {
      reveal(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        window.setTimeout(() => reveal(true), TABLE_ROW_REVEAL_DELAY_MS);
      },
      { root, ...gameRowRevealObserverOptions() },
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, [animateRows, animationCoordinator, index, isVisuallyMounted, revealKey, revealedKeys, scrollRoot]);

  useLayoutEffect(() => {
    if (renderWithoutAnimation) return;
    if (!revealed) {
      animationControls.set({ rotateX: -90 });
      return;
    }

    const settleTransform = () => {
      if (!rowRef.current) return;
      rowRef.current.style.transform = "none";
      rowRef.current.style.willChange = "auto";
    };
    if (!shouldAnimate || prefersReducedMotion) {
      animationControls.set({ rotateX: 0 });
      settleTransform();
      return;
    }

    const runAnimation = async (coordinatedDuration?: number, coordinatedBounce = 0) => {
      const generation = ++animationGeneration.current;
      await animationControls.start(
        { rotateX: 0 },
        coordinatedDuration != null
          ? {
              type: "spring",
              visualDuration: coordinatedDuration,
              bounce: coordinatedBounce,
            }
          : {
              type: "spring",
              stiffness: 220,
              damping: 20,
              mass: 1,
              restSpeed: 0.15,
              restDelta: 0.08,
            },
      );
      if (generation !== animationGeneration.current) return;
      settleTransform();
    };

    const runner = (duration: number, bounce: number) => runAnimation(duration, bounce);
    animationCoordinator.runners.set(revealKey, runner);
    if (animationCoordinator.durations.has(revealKey)) {
      if (animationCoordinator.pendingFinalReveal) {
        requestCoordinatedFinalReveal(animationCoordinator);
      }
      void runner(
        animationCoordinator.durations.get(revealKey) ?? 0.24,
        animationCoordinator.bounces.get(revealKey) ?? 0.34,
      );
    } else {
      void runAnimation();
    }

    return () => {
      animationGeneration.current += 1;
      animationCoordinator.runners.delete(revealKey);
    };
  }, [
    animationControls,
    animationCoordinator,
    prefersReducedMotion,
    renderWithoutAnimation,
    revealKey,
    revealed,
    shouldAnimate,
  ]);

  return (
    <MotionTableRow
      ref={rowRef}
      data-reveal-animation={shouldAnimate ? "hinge" : "none"}
      initial={{ rotateX: revealed || renderWithoutAnimation ? 0 : -90 }}
      animate={renderWithoutAnimation ? { rotateX: 0, transition: { duration: 0 } } : animationControls}
      style={{
        transformOrigin: "50% 0%",
        transformPerspective: 640,
        backfaceVisibility: "hidden",
      }}
      sx={{
        ...(onClick ? { position: { xs: "relative", sm: "static" } } : {}),
        height: { xs: 56, sm: GAME_ROW_HEIGHT },
        visibility: renderWithoutAnimation || revealed ? "visible" : "hidden",
        willChange: shouldAnimate && revealed ? "transform" : "auto",
        cursor: onClick ? { xs: "pointer", sm: "default" } : "default",
      }}
      onClick={(event) => {
        if (ignoreNextClick.current) return;
        onClick?.(event);
      }}
      onKeyDown={onKeyDown}
      onTouchStart={(event) => {
        touchStartY.current = event.touches[0]?.clientY ?? null;
        ignoreNextClick.current = false;
      }}
      onTouchMove={(event) => {
        const startY = touchStartY.current;
        const currentY = event.touches[0]?.clientY;
        if (startY != null && currentY != null && Math.abs(currentY - startY) > 8) {
          ignoreNextClick.current = true;
        }
      }}
      onTouchEnd={() => {
        touchStartY.current = null;
        if (ignoreNextClick.current) {
          window.setTimeout(() => {
            ignoreNextClick.current = false;
          }, 350);
        }
      }}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
    >
      {children}
    </MotionTableRow>
  );
};

const responsiveCellWidths = {
  "& tr > :nth-of-type(1)": { width: { sm: "10%", lg: "9%" } },
  "& tr > :nth-of-type(2)": { width: { xs: "43%", sm: "14%" } },
  "& tr > :nth-of-type(3)": { width: { xs: "20%", sm: "10%", lg: "9%" } },
  "& tr > :nth-of-type(4)": { width: { xs: "25%", sm: "10%", lg: "8%" } },
  "& tr > :nth-of-type(5)": { width: { sm: "20%", lg: "24%" } },
  "& tr > :nth-of-type(6)": { width: { lg: "10%" } },
  "& tr > :nth-of-type(7)": { width: { xs: "12%", sm: "14%", lg: "11%" } },
  "& tr > :nth-of-type(8)": {
    display: { xs: "none", sm: "table-cell" },
    width: { sm: "22%", lg: "15%" },
    minWidth: { sm: GAME_TABLE_ACTION_MIN_WIDTH },
  },
} as const;
const responsiveColumnWidths = {
  "& col:nth-of-type(1), & col:nth-of-type(5)": { display: { xs: "none", sm: "table-column" } },
  "& col:nth-of-type(6)": { display: { xs: "none", lg: "table-column" }, width: { sm: "10%" } },
  "& col:nth-of-type(1)": { width: { sm: "10% !important", lg: "9% !important" } },
  "& col:nth-of-type(2)": { width: { xs: "43% !important", sm: "14% !important" } },
  "& col:nth-of-type(3)": { width: { xs: "20% !important", sm: "10% !important", lg: "9% !important" } },
  "& col:nth-of-type(4)": { width: { xs: "25% !important", sm: "10% !important", lg: "8% !important" } },
  "& col:nth-of-type(5)": { width: { sm: "20% !important", lg: "24% !important" } },
  "& col:nth-of-type(7)": { width: { xs: "12% !important", sm: "14% !important", lg: "11% !important" } },
  "& col:nth-of-type(8)": {
    display: { xs: "none", sm: "table-column" },
    width: { sm: "22% !important", lg: "15% !important" },
    minWidth: { sm: GAME_TABLE_ACTION_MIN_WIDTH },
  },
} as const;

const TimeControlTable = ({
  label,
  games,
  onAnalyze,
  analysisAction,
  sort,
  onSort,
  onLoadMore,
  hasMoreGames,
  isLoadingMore,
  isVisuallyMounted,
  animateRows,
  isSearchFiltering,
}: TimeControlTableProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const openReview = (gameId: string) => {
    void navigate(`/games/${gameId}`, { state: { gamesOrigin: gamesOrigin(location) } });
  };
  const isMobile = useBreakpointDown("sm");
  const bodyTableRef = useRef<HTMLTableElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [tableScrollElement, setTableScrollElement] = useState<HTMLDivElement | null>(null);
  useRememberedScroll("games:table", tableScrollElement, isVisuallyMounted && games.length > 0);
  const captureTableScroll = useCallback((element: HTMLDivElement | null) => {
    tableScrollRef.current = element;
    setTableScrollElement(element);
  }, []);
  const revealedGameIds = useRef(new Set<string>());
  const revealAnimationCoordinator = useRef(createRevealAnimationCoordinator()).current;
  const [bodyHeight, setBodyHeight] = useState(TABLE_BODY_HEIGHT);
  const sortedGames = useMemo(() => {
    const effectiveSort = sort ?? { key: "date" as const, direction: "desc" as const };
    return [...games].sort((left, right) => {
      const leftValue = sortValue(left, effectiveSort.key);
      const rightValue = sortValue(right, effectiveSort.key);
      const comparison =
        typeof leftValue === "string" && typeof rightValue === "string"
          ? leftValue.localeCompare(rightValue)
          : Number(leftValue) - Number(rightValue);
      return effectiveSort.direction === "asc" ? comparison : -comparison;
    });
  }, [games, sort]);

  useLayoutEffect(() => {
    const table = bodyTableRef.current;
    if (!table) return;

    const measureRows = () => {
      const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>("tbody tr:not([data-trailing-spacer])"));
      const finalVisibleRow = rows[Math.min(rows.length, VISIBLE_GAME_ROWS) - 1];
      if (!finalVisibleRow) return;
      const measuredHeight = Math.ceil(finalVisibleRow.offsetTop + finalVisibleRow.offsetHeight);
      setBodyHeight((current) => (current === measuredHeight ? current : measuredHeight));
    };

    measureRows();
    const observer = new ResizeObserver(measureRows);
    observer.observe(table);
    return () => observer.disconnect();
  }, [sortedGames]);

  useEffect(() => {
    const tableScrollElement = tableScrollRef.current;
    if (!tableScrollElement) return;

    let previousTop = tableScrollElement.scrollTop;
    let previousAt = performance.now();
    let settleTimer: number | undefined;
    const trackScrollVelocity = () => {
      const now = performance.now();
      const elapsed = Math.max(1, now - previousAt);
      const currentTop = tableScrollElement.scrollTop;
      const instantaneousVelocity = Math.abs(currentTop - previousTop) / elapsed;
      revealAnimationCoordinator.scrollVelocity =
        instantaneousVelocity * 0.72 + revealAnimationCoordinator.scrollVelocity * 0.28;
      revealAnimationCoordinator.lastScrollAt = now;
      previousTop = currentTop;
      previousAt = now;
      if (settleTimer != null) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        requestCoordinatedFinalReveal(revealAnimationCoordinator);
        revealAnimationCoordinator.scrollVelocity = 0;
      }, 130);
    };

    tableScrollElement.addEventListener("scroll", trackScrollVelocity, {
      passive: true,
    });
    return () => {
      tableScrollElement.removeEventListener("scroll", trackScrollVelocity);
      if (settleTimer != null) window.clearTimeout(settleTimer);
      if (revealAnimationCoordinator.finalRevealTimer != null) {
        window.clearTimeout(revealAnimationCoordinator.finalRevealTimer);
        revealAnimationCoordinator.finalRevealTimer = undefined;
      }
    };
  }, [revealAnimationCoordinator]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, height: "100%" }}>
      {/* Section title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          mb: 0.75,
          mx: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "primary.main",
            fontWeight: 700,
            letterSpacing: "0.06em",
          }}
        >
          {label.toUpperCase()}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {games.length}
        </Typography>
      </Box>

      <Paper
        data-games-table-frame
        elevation={0}
        sx={{
          height: "auto",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "visible",
          border: "none",
          borderRadius: 0,
          bgcolor: "background.default",
        }}
      >
        <Table
          data-testid="games-table-header"
          size="small"
          sx={{
            mx: 0,
            width: "100%",
            maxWidth: "100%",
            bgcolor: "background.default",
            borderCollapse: "separate",
            borderSpacing: 0,
            boxShadow: "0 3px 0 #0a0a0a",
            position: "relative",
            tableLayout: "fixed",
            zIndex: 1,
            ...responsiveCellWidths,
            "& thead tr > :first-of-type": {
              borderLeft: "1px solid rgba(255,255,255,0.15) !important",
              borderRight: "0 !important",
            },
            "& thead tr > :last-child": {
              borderRight: "1px solid rgba(255,255,255,0.15) !important",
              borderLeft: "0 !important",
            },
            "& thead tr > :nth-of-type(7)": {
              borderRight: { xs: "1px solid rgba(255,255,255,0.15) !important", sm: "0 !important" },
            },
            "& thead tr > :nth-of-type(2)": {
              borderLeft: { xs: "1px solid rgba(255,255,255,0.15) !important", sm: "0 !important" },
            },
            ...responsiveColumnWidths,
          }}
        >
          <colgroup>
            {COL_WIDTHS.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <SortableHeader label="Result" sort={sort} sortKey="result" onSort={onSort} hiddenOnMobile />
              <SortableHeader label="Opponent" sort={sort} sortKey="opponent" onSort={onSort} />
              <SortableHeader
                label="Rating"
                sort={sort}
                sortKey="rating"
                onSort={onSort}
                align="right"
                mobileLabel="Elo"
              />
              <SortableHeader label="Date" sort={sort} sortKey="date" onSort={onSort} />
              <SortableHeader label="Opening" sort={sort} sortKey="opening" onSort={onSort} hiddenOnMobile />
              <TableCell sx={{ ...headerSx, ...wideDesktopSx }}>Format</TableCell>
              <SortableHeader
                label="Accuracy"
                sort={sort}
                sortKey="accuracy"
                onSort={onSort}
                align="center"
                mobileLabel="Acc"
              />
              <TableCell
                aria-hidden
                sx={{
                  ...headerSx,
                  ...hiddenOnMobileSx,
                  minWidth: { sm: GAME_TABLE_ACTION_MIN_WIDTH },
                  width: { sm: "22%", lg: "15%" },
                }}
              />
            </TableRow>
          </TableHead>
        </Table>
        <TableContainer
          component="div"
          ref={captureTableScroll}
          data-lenis-prevent
          data-lenis-scroll
          data-lenis-table
          onScroll={(event) => {
            const element = event.currentTarget;
            if (
              hasMoreGames &&
              element.scrollTop + element.clientHeight >=
                element.scrollHeight - GAME_ROW_HEIGHT * GAME_PREFETCH_REMAINING_ROWS
            ) {
              onLoadMore();
            }
          }}
          sx={{
            height: "auto",
            flex: 1,
            minHeight: 0,
            maxHeight: bodyHeight + GAME_ROW_TRANSFORM_GUTTER + 4,
            width: `calc(100% + ${GAME_ROW_TRANSFORM_GUTTER * 2}px)`,
            maxWidth: "none",
            mx: `-${GAME_ROW_TRANSFORM_GUTTER}px`,
            mt: 0,
            px: `${GAME_ROW_TRANSFORM_GUTTER}px`,
            pt: "4px",
            pb: `${GAME_ROW_TRANSFORM_GUTTER}px`,
            boxSizing: "border-box",
            contain: "layout",
            overflowX: "hidden",
            overflowY: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <Table
            data-testid="games-table-body"
            size="small"
            ref={bodyTableRef}
            sx={{
              bgcolor: "background.default",
              width: "100%",
              maxWidth: "100%",
              borderCollapse: "separate",
              borderSpacing: "0 4px",
              tableLayout: "fixed",
              overflow: "visible",
              ...responsiveCellWidths,
              "& tbody tr td": {
                bgcolor: "background.paper",
                borderTop: "1px solid rgba(255,255,255,0.1) !important",
              },
              "& tbody tr > :first-of-type": {
                borderLeft: "1px solid rgba(255,255,255,0.1)",
              },
              "& tbody tr > :nth-of-type(2)": {
                borderLeft: { xs: "1px solid rgba(255,255,255,0.1)", sm: "none" },
              },
              "& tbody tr > :last-child": {
                borderRight: "1px solid rgba(255,255,255,0.1)",
              },
              "& tbody tr > :nth-of-type(7)": {
                borderRight: { xs: "1px solid rgba(255,255,255,0.1)", sm: "none" },
              },
              "& tbody tr:last-child td": {
                borderBottom: "1px solid rgba(255,255,255,0.1) !important",
                boxShadow: "inset 0 -1px rgba(255,255,255,0.1)",
              },
              ...responsiveColumnWidths,
            }}
          >
            <colgroup>
              {COL_WIDTHS.map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
            <TableBody>
              {isSearchFiltering
                ? Array.from({ length: VISIBLE_GAME_ROWS }, (_, index) => (
                    <TableRow key={index} sx={{ height: GAME_ROW_HEIGHT }}>
                      {COL_WIDTHS.map((_, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          sx={{
                            ...bodySx,
                            ...([0, 4].includes(cellIndex) ? hiddenOnMobileSx : {}),
                            ...([5, 6].includes(cellIndex) ? wideDesktopSx : {}),
                          }}
                        >
                          <Skeleton
                            animation="wave"
                            sx={{
                              bgcolor: "rgba(240,236,226,0.08)",
                              transform: "none",
                              ...(cellIndex === 6 ? { ml: "auto", width: "65%" } : {}),
                            }}
                            width={cellIndex === 0 ? "40%" : cellIndex === 2 || cellIndex === 5 ? "55%" : "85%"}
                            height={12}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : sortedGames.map((game, index) => (
                    <RevealingGameRow
                      key={game.id}
                      index={index}
                      revealKey={game.id}
                      revealedKeys={revealedGameIds.current}
                      scrollRoot={tableScrollElement}
                      animationCoordinator={revealAnimationCoordinator}
                      isVisuallyMounted={isVisuallyMounted}
                      animateRows={animateRows}
                      onClick={
                        isMobile && game.rules === "chess"
                          ? (event) => {
                              if ((event.target as HTMLElement).closest("a,button")) return;
                              if (game.analyzed && (game.analysisDepth ?? 0) > 0) openReview(game.id);
                              else onAnalyze(game);
                            }
                          : undefined
                      }
                      onKeyDown={
                        isMobile && game.rules === "chess"
                          ? (event) => {
                              if ((event.target as HTMLElement).closest("a,button")) return;
                              if (event.key !== "Enter" && event.key !== " ") return;
                              event.preventDefault();
                              if (game.analyzed && (game.analysisDepth ?? 0) > 0) openReview(game.id);
                              else onAnalyze(game);
                            }
                          : undefined
                      }
                      tabIndex={isMobile && game.rules === "chess" ? 0 : undefined}
                      ariaLabel={
                        isMobile && game.rules === "chess"
                          ? `${game.analyzed && (game.analysisDepth ?? 0) > 0 ? "Review" : "Analyze"} game against ${game.opponent}`
                          : undefined
                      }
                    >
                      <TableCell
                        sx={{
                          ...bodySx,
                          ...hiddenOnMobileSx,
                          fontWeight: 700,
                          color: resultColor(game.result),
                        }}
                      >
                        {game.result === "win" ? "W" : game.result === "loss" ? "L" : "D"}
                      </TableCell>

                      <TableCell sx={bodySx}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              flexShrink: 0,
                              bgcolor: game.playerColor === "w" ? "#444" : "#f0ece2",
                              border: "1px solid rgba(255,255,255,0.25)",
                            }}
                          />
                          <Typography
                            component="a"
                            href={`https://www.chess.com/member/${game.opponent}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            noWrap
                            sx={{
                              color: "text.primary",
                              fontWeight: 500,
                              ...(isMobile ? { position: "relative", zIndex: 2 } : {}),
                              textDecoration: "none",
                              "&:hover": {
                                color: "primary.main",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {game.opponent}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            display: { xs: "none", md: "block" },
                            mt: 0.2,
                            color: "text.secondary",
                            fontSize: ".56rem",
                            fontWeight: 750,
                            letterSpacing: ".045em",
                            whiteSpace: "nowrap",
                          }}
                        >
                          OPP {game.opponentRating || "—"} · YOU PLAYED {game.playerColor === "w" ? "WHITE" : "BLACK"}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{
                          ...bodySx,
                          textAlign: "right",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: ratingChangeColor(game.ratingChange),
                            fontWeight: 700,
                            mr: 0.75,
                          }}
                        >
                          {ratingChangePrefix(game.ratingChange)}
                        </Box>
                        <Box component="span">{game.playerRating || "—"}</Box>
                      </TableCell>

                      <TableCell sx={{ ...bodySx, color: "text.secondary" }}>
                        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                          {formatDate(game.playedAt)}
                        </Box>
                        <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                          {formatCompactGameDate(game.playedAt)}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ ...bodySx, ...hiddenOnMobileSx }}>
                        {game.eco || game.openingName ? (
                          <Typography
                            component="a"
                            href={game.eco ?? game.chessComUrl ?? undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            noWrap
                            sx={{
                              color: "text.secondary",
                              textDecoration: "none",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              "&:hover": {
                                color: "primary.main",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {game.openingName
                              ? conciseOpeningName(game.openingName)
                              : game.eco
                                ? ecoName(game.eco)
                                : "Opening"}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.15)" }}>
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell
                        data-testid="game-format-cell"
                        aria-label={gameFormatEvidence({
                          timeControl: game.timeControl,
                          exactTimeControl: game.exactTimeControl ? tableClockLabel(game.exactTimeControl) : null,
                          rated: game.rated,
                        })}
                        sx={{ ...bodySx, ...wideDesktopSx, overflow: "hidden" }}
                      >
                        <Typography
                          sx={{
                            color: "text.primary",
                            fontSize: ".55rem",
                            fontWeight: 800,
                            lineHeight: 1.1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {game.timeControl.toUpperCase()} ·{" "}
                          {game.rated === true ? "RATED" : game.rated === false ? "CASUAL" : "UNKNOWN"}
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.2,
                            color: "text.secondary",
                            fontSize: ".52rem",
                            lineHeight: 1.1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tableClockLabel(game.exactTimeControl)}
                        </Typography>
                      </TableCell>
                      <AccuracyCell game={game} />

                      <TableCell
                        data-testid="game-action-cell"
                        sx={{
                          ...bodySx,
                          ...hiddenOnMobileSx,
                          pl: { xs: 0.75, sm: 1.5 },
                          pr: { xs: 1.5, sm: 1.25 },
                          textAlign: "right",
                          whiteSpace: "nowrap",
                          minWidth: { sm: GAME_TABLE_ACTION_MIN_WIDTH },
                          width: { sm: "22%", lg: "15%" },
                        }}
                      >
                        {game.rules !== "chess" ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.55rem",
                            }}
                          >
                            {game.rules.toUpperCase()}
                          </Typography>
                        ) : isMobile ? null : game.analyzed && (game.analysisDepth ?? 0) > 0 ? (
                          <Button
                            aria-label={`Review game against ${game.opponent}`}
                            size="small"
                            disableRipple
                            onClick={() => openReview(game.id)}
                            sx={GAME_TABLE_ACTION_SX}
                          >
                            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                              REVIEW
                            </Box>
                            <Box component="span" aria-hidden sx={{ display: { xs: "inline", sm: "none" } }}>
                              →
                            </Box>
                          </Button>
                        ) : (
                          <Button
                            aria-label={`Analyze game against ${game.opponent}`}
                            size="small"
                            disableRipple
                            disabled={!analysisAction(game).allowed && !analysisAction(game).cta}
                            onClick={() => onAnalyze(game)}
                            sx={GAME_TABLE_ACTION_SX}
                          >
                            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                              {analysisAction(game).allowed
                                ? "ANALYZE"
                                : analysisAction(game).cta === "manage_trial"
                                  ? "MANAGE TRIAL"
                                  : analysisAction(game).cta === "join_club"
                                    ? "JOIN CLUB"
                                    : "CHECKING…"}
                            </Box>
                            <Box component="span" aria-hidden sx={{ display: { xs: "inline", sm: "none" } }}>
                              →
                            </Box>
                          </Button>
                        )}
                      </TableCell>
                    </RevealingGameRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          data-testid="games-library-status"
          aria-live="polite"
          sx={{
            alignItems: "center",
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexShrink: 0,
            gap: 1.25,
            minHeight: 38,
            px: 1.5,
          }}
        >
          <Box
            aria-hidden
            sx={{
              bgcolor: isLoadingMore ? "secondary.main" : hasMoreGames ? "primary.main" : "text.secondary",
              height: 7,
              width: 7,
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.08em" }}
          >
            {isLoadingMore
              ? `READING NEXT GAMES · ${games.length} READY`
              : hasMoreGames
                ? `KEEP SCROLLING · ${games.length} GAMES READY`
                : `LIBRARY CAUGHT UP · ${games.length} GAME${games.length === 1 ? "" : "S"}`}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export const GamesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const defaults = useUserSettingsStore((state) => state.settings);
  const [savedFlow, setFlow] = useRememberedPageState<GamesFlowState>(
    "games",
    {
      search: "",
      selectedCategory: defaults.gamesDefaultFormat,
      selectedClock: defaults.gamesDefaultClock,
      sort: defaults.gamesDefaultSort,
      filter: defaults.gamesDefaultFilter,
    },
    isGamesFlow,
  );
  const [gameFilter, setGameFilter] = useState(savedFlow.filter ?? defaults.gamesDefaultFilter);
  const isSelectionCached = useGamesStore((state) => state.isSelectionCached);
  const importProgress = useGamesStore((state) => state.importProgress);
  const requestedCategory = useMemo(() => {
    const requested = new URLSearchParams(location.search).get("format");
    return GAME_CATEGORIES.some(({ id }) => id === requested) ? (requested as GameCategory) : null;
  }, [location.search]);
  const initialCategory = requestedCategory ?? savedFlow.selectedCategory;
  const initialFilters = useMemo(
    () => ({
      analyzed: gameFilter === "analyzed" ? true : undefined,
      timeControl: initialCategory && initialCategory !== "all" ? initialCategory : undefined,
      exactTimeControl:
        savedFlow.selectedCategory === initialCategory && savedFlow.selectedClock !== "all"
          ? (savedFlow.selectedClock ?? undefined)
          : undefined,
    }),
    [gameFilter, initialCategory, savedFlow.selectedCategory, savedFlow.selectedClock],
  );
  const restoredClock = savedFlow.selectedCategory === initialCategory ? savedFlow.selectedClock : null;
  const {
    games,
    isLoading,
    importStatus,
    search,
    setSearch,
    filtered,
    summary,
    markGameAnalyzed,
    loadMoreGames,
    loadGamesForSelection,
    hasMoreGames,
    isLoadingMore,
    streamProgress,
  } = useGames(savedFlow.search, initialFilters);

  const analysisModalRef = useRef<GamesAnalysisModalHandle>(null);
  const [sort, setSort] = useState<GameSort | null>(savedFlow.sort);
  const [isSearchFiltering, setIsSearchFiltering] = useState(false);
  const [isSortTransitioning, setIsSortTransitioning] = useState(false);
  const sortTransitionTimer = useRef<number | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<GameCategory | null>(initialCategory);
  const [selectedClock, setSelectedClock] = useState<string | null>(restoredClock);
  const [loadingClock, setLoadingClock] = useState<string | null>(restoredClock);
  const [restoringClock, setRestoringClock] = useState<string | null>(restoredClock);
  const previousRequestedCategory = useRef(requestedCategory);
  useEffect(() => {
    if (previousRequestedCategory.current === requestedCategory) return;
    previousRequestedCategory.current = requestedCategory;
    if (!requestedCategory) return;
    setSelectedCategory(requestedCategory);
    setSelectedClock(null);
    setLoadingClock(null);
    setRestoringClock(null);
  }, [requestedCategory]);
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const analysisAction = useCallback(
    (game: GameListItem) => resolveAnalysisAction(entitlementSnapshot, game.id, game.analyzed, game.exactTimeControl),
    [entitlementSnapshot],
  );
  const openAnalysisModal = useCallback(
    (game: GameListItem) => {
      const gate = analysisAction(game);
      if (gate.allowed) analysisModalRef.current?.open(game.id);
      else if (gate.cta === "manage_trial") void navigate("/subscription#trial");
      else if (gate.cta === "join_club") void navigate("/subscription");
    },
    [analysisAction, navigate],
  );
  const activeStep = selectedClock != null ? 3 : selectedCategory === "all" ? 1 : selectedCategory != null ? 2 : 1;

  useEffect(() => {
    if (!restoringClock || isLoading) return;

    const frame = window.requestAnimationFrame(() => {
      startTransition(() => {
        setSelectedClock(restoringClock);
        setLoadingClock(null);
        setRestoringClock(null);
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isLoading, restoringClock]);

  useEffect(() => {
    setFlow({
      filter: gameFilter,
      search,
      selectedCategory,
      selectedClock,
      sort,
    });
  }, [gameFilter, search, selectedCategory, selectedClock, setFlow, sort]);

  useEffect(
    () => () => {
      if (sortTransitionTimer.current != null) {
        window.clearTimeout(sortTransitionTimer.current);
      }
    },
    [],
  );

  const handleSort = (key: GameSortKey) => {
    setIsSortTransitioning(true);
    window.requestAnimationFrame(() => {
      startTransition(() => {
        setSort((current) =>
          current?.key === key
            ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
            : { key, direction: "asc" },
        );
      });
      if (sortTransitionTimer.current != null) {
        window.clearTimeout(sortTransitionTimer.current);
      }
      sortTransitionTimer.current = window.setTimeout(() => {
        setIsSortTransitioning(false);
        sortTransitionTimer.current = undefined;
      }, 220);
    });
  };
  const revealTables = useCallback((clock: string) => {
    startTransition(() => {
      setSelectedClock(clock);
      setLoadingClock(null);
    });
  }, []);
  const selectClock = useCallback(
    (clock: string, filters: GameListFilters) => {
      const scopedFilters = {
        ...filters,
        analyzed: gameFilter === "analyzed" ? true : undefined,
        timeControl: selectedCategory === "all" ? undefined : (selectedCategory ?? undefined),
      };
      if (isSelectionCached(scopedFilters)) {
        revealTables(clock);
        return;
      }

      setLoadingClock(clock);
      const request = loadGamesForSelection(scopedFilters);
      void (async () => {
        try {
          await request;
          revealTables(clock);
        } catch {
          setLoadingClock(null);
        }
      })();
    },
    [gameFilter, isSelectionCached, loadGamesForSelection, revealTables, selectedCategory],
  );
  const categoryGames =
    selectedCategory == null
      ? []
      : games.filter((game) => selectedCategory === "all" || game.timeControl === selectedCategory);
  const countKey = gameFilter === "analyzed" ? "analyzedCount" : "count";
  const totalCount = summary?.[gameFilter === "analyzed" ? "totalAnalyzedGames" : "totalGames"] ?? games.length;
  const categoryGameCount =
    selectedCategory == null
      ? 0
      : selectedCategory === "all"
        ? totalCount
        : (summary?.formats.find((format) => format.timeControl === selectedCategory)?.[countKey] ??
          categoryGames.length);
  const clockCounts = new Map<string, number>();
  summary?.clocks
    .filter((clock) => selectedCategory === "all" || clock.timeControl === selectedCategory)
    .forEach((clock) => {
      const key = clockGroupKey(clock.exactTimeControl);
      clockCounts.set(key, (clockCounts.get(key) ?? 0) + clock[countKey]);
    });
  const sortedCategories = GAME_CATEGORIES.map((category) => ({
    ...category,
    count:
      category.id === "all"
        ? totalCount
        : (summary?.formats.find((format) => format.timeControl === category.id)?.[countKey] ??
          games.filter((game) => game.timeControl === category.id).length),
  })).sort((left, right) => {
    if (left.id === "all") return 1;
    if (right.id === "all") return -1;
    return right.count - left.count || left.label.localeCompare(right.label);
  });
  const availableClocks = [...clockCounts.keys()]
    .filter((clock) => clock !== "unknown" && (gameFilter !== "analyzed" || (clockCounts.get(clock) ?? 0) > 0))
    .sort((left, right) => {
      const leftCount = clockCounts.get(left) ?? 0;
      const rightCount = clockCounts.get(right) ?? 0;
      return rightCount - leftCount || left.localeCompare(right, undefined, { numeric: true });
    });

  useEffect(() => {
    if (selectedClock === "unknown") setSelectedClock(null);
  }, [selectedClock]);
  const tableClock = selectedClock;
  const clockGames =
    tableClock == null
      ? []
      : categoryGames.filter((game) => tableClock === "all" || clockGroupKey(game.exactTimeControl) === tableClock);
  const filteredGameIds = new Set(filtered.map((game) => game.id));
  const selectedGameCount =
    selectedClock === "all" ? categoryGameCount : selectedClock == null ? 0 : (clockCounts.get(selectedClock) ?? 0);
  const selectedGames = clockGames.filter((game) => filteredGameIds.has(game.id));
  const tableGroups =
    selectedCategory === "all"
      ? [{ timeControl: "all", label: "All games", games: selectedGames }]
      : [
          ...selectedGames.reduce((groups, game) => {
            const existing = groups.get(game.timeControl) ?? [];
            existing.push(game);
            groups.set(game.timeControl, existing);
            return groups;
          }, new Map<string, GameListItem[]>()),
        ]
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([timeControl, gamesForControl]) => ({
            timeControl,
            label: GAME_CATEGORIES.find((category) => category.id === timeControl)?.label ?? timeControl,
            games: gamesForControl,
          }));

  // If user has no profile, show a message and disable imports
  if (profile === null && !isLoading) {
    return (
      <Box sx={{ minHeight: layoutTokens.screenMinHeight, p: 3 }}>
        <Typography variant="h6" sx={{ color: "primary.main", mb: 2 }}>
          Profile Not Linked
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
          Please go to your{" "}
          <RouterLink to="/profile" style={{ color: "#f0ece2", textDecoration: "underline" }}>
            profile page
          </RouterLink>{" "}
          to link your Chess.com account before importing games.
        </Typography>
      </Box>
    );
  }

  const activeCategory = GAME_CATEGORIES.find((category) => category.id === selectedCategory);
  const selectionSteps = [
    { key: "format", label: "Format", receipt: activeCategory?.label },
    {
      key: "clock",
      label: "Clock",
      skipped: selectedCategory === "all",
      receipt:
        selectedClock && selectedClock !== "all"
          ? tableClockLabel(selectedClock)
          : selectedClock === "all"
            ? "All clocks"
            : null,
    },
    { key: "games", label: "Games", receipt: activeStep === 3 ? `${selectedGameCount} games` : null },
  ] as const;
  const tourStep = activeStep === 1 ? "format" : activeStep === 2 ? "clock" : "results";

  return (
    <Box
      data-tour-layer={`games:${tourStep}`}
      data-tour={`games-${tourStep}`}
      sx={{
        height: activeStep === 3 ? { xs: "calc(100% - var(--mobile-app-bar-height, 44px))", md: "100%" } : "auto",
        minHeight: activeStep === 3 ? 0 : layoutTokens.screenMinHeight,
        position: activeStep === 3 ? "absolute" : "relative",
        inset: activeStep === 3 ? 0 : undefined,
        width: "100%",
        maxWidth: layoutTokens.workspaceMaxWidth,
        mx: "auto",
        boxSizing: "border-box",
        overflow: activeStep === 3 ? "hidden" : undefined,
        overflowX: "clip",
        p: layoutTokens.workspacePagePadding,
        display: "flex",
        flexDirection: "column",
        gap: activeStep === 3 ? { xs: 1.5, md: 2 } : layoutTokens.sectionGap,
      }}
    >
      {importProgress && (
        <Box
          sx={{
            p: 1.5,
            bgcolor: "background.paper",
            border: "1px solid rgba(240,236,226,0.15)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "primary.main",
              fontWeight: 700,
              display: "block",
              mb: 0.5,
            }}
          >
            Nikola has brought in {importProgress.imported}
            {importProgress.target != null ? ` / ${importProgress.target}` : ""} games.
          </Typography>
          <LinearProgress
            variant={importProgress.target == null ? "indeterminate" : "determinate"}
            value={importProgress.target ? (importProgress.imported / importProgress.target) * 100 : undefined}
            sx={{
              height: 4,
              bgcolor: "rgba(255,255,255,0.05)",
              "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
            }}
          />
        </Box>
      )}

      {importStatus && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {importStatus}
        </Typography>
      )}

      {summary?.totalGames === 0 ? (
        <Box
          sx={{
            py: { xs: 8, md: 14 },
            textAlign: "center",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="h6" sx={{ color: "primary.main", mb: 1 }}>
            YOUR LIBRARY IS READY
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Import finished Chess.com games on Analyze to begin building verified evidence.
          </Typography>
          <Button
            onClick={() => void navigate("/analyze")}
            sx={{
              mt: 3,
              minHeight: 44,
              border: "2px solid",
              borderColor: "primary.main",
              borderRadius: 0,
              color: "primary.main",
              fontSize: ".68rem",
              fontWeight: 950,
              "&:hover": { bgcolor: "primary.main", color: "background.default" },
            }}
          >
            GO TO ANALYZE →
          </Button>
        </Box>
      ) : (
        <SelectionFlow
          activeStep={activeStep}
          ariaLabel="Game selection steps"
          hideSelectionSteps={defaults.performanceScopeCollapsed}
          steps={selectionSteps}
          navigationDisabled={loadingClock != null}
          onStep={(step) => {
            if (step === "format") {
              setSelectedCategory(null);
              setSelectedClock(null);
            } else if (step === "clock") {
              setSelectedClock(null);
            }
          }}
        >
          <>
            {activeStep === 1 && (
              <Box>
                <SelectionStepIntro
                  eyebrow="STEP 01 / FORMAT"
                  title={
                    <>
                      What kind of game
                      <br />
                      do you want to study?
                    </>
                  }
                  description="Choose one format. Your library keeps every date and separates each clock precisely."
                  descriptionMaxWidth={520}
                />
                <fieldset
                  data-tour="games-format-picker"
                  disabled={loadingClock != null}
                  style={{ border: 0, margin: 0, minWidth: 0, padding: 0 }}
                >
                  <FormatPicker
                    ariaLabel="Game format"
                    value={selectedCategory}
                    onChange={(category) => {
                      setSelectedCategory(category);
                      setSelectedClock(null);
                    }}
                    items={sortedCategories
                      .filter(({ id, count }) => id !== "all" && (gameFilter !== "analyzed" || count > 0))
                      .map(({ id, label, count }) => ({
                        id,
                        label,
                        count,
                        progress:
                          id === "all" && loadingClock === "all" && streamProgress
                            ? { current: streamProgress.loaded, required: streamProgress.total }
                            : undefined,
                      }))}
                  />
                </fieldset>
              </Box>
            )}

            {activeStep === 2 && selectedCategory != null && (
              <Box>
                <SelectionStepIntro
                  eyebrow="STEP 02 / CLOCK"
                  title={
                    <>
                      {activeCategory?.label} games,
                      <br />
                      at what pace?
                    </>
                  }
                  description="Choose an exact clock to focus your review."
                />
                <Box
                  data-tour="games-clock-picker"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(auto-fit, minmax(180px, 1fr))",
                    },
                    gap: 1.5,
                  }}
                >
                  {availableClocks.map((clock) => (
                    <SelectionClockCard
                      key={clock}
                      active={selectedClock === clock}
                      count={clockCounts.get(clock) ?? 0}
                      glyph={clockSignatureGlyph(clock)}
                      label={clockSignatureLabel(clock)}
                      onClick={() => {
                        const filters = {
                          timeControl: selectedCategory === "all" ? undefined : selectedCategory,
                          exactTimeControl: clock,
                        };
                        selectClock(clock, filters);
                      }}
                      loadingProgress={loadingClock === clock ? streamProgress : null}
                      disabled={loadingClock != null}
                    />
                  ))}
                  {(gameFilter !== "analyzed" || categoryGameCount > 0) && (
                    <SelectionClockCard
                      active={selectedClock === "all"}
                      count={categoryGameCount}
                      glyph="∞"
                      label="All clocks"
                      onClick={() => {
                        const filters = {
                          timeControl: selectedCategory === "all" ? undefined : selectedCategory,
                        };
                        selectClock("all", filters);
                      }}
                      loadingProgress={loadingClock === "all" ? streamProgress : null}
                      disabled={loadingClock != null}
                    />
                  )}
                </Box>
              </Box>
            )}

            {activeStep === 3 && selectedCategory != null && selectedClock != null && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  mx: 0,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    alignItems: { xs: "stretch", md: "flex-end" },
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: { xs: 1.5, md: 3 },
                    justifyContent: "space-between",
                    pb: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "primary.main",
                        fontWeight: 800,
                        letterSpacing: "0.1em",
                      }}
                    >
                      STEP 03 / GAMES
                    </Typography>
                    <Typography
                      sx={{
                        color: "text.primary",
                        fontWeight: 800,
                        letterSpacing: "-0.04em",
                        fontSize: { xs: "2rem", md: "2.5rem" },
                        lineHeight: 1,
                        mt: 0.75,
                      }}
                    >
                      {selectedCategory === "all"
                        ? "All games"
                        : `${activeCategory?.label} · ${selectedClock === "all" ? "All clocks" : tableClockLabel(selectedClock)}`}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "block",
                        mt: 1,
                      }}
                    >
                      {selectedGameCount} imported game
                      {selectedGameCount === 1 ? "" : "s"} in this selection
                    </Typography>
                  </Box>
                  <DebouncedGameSearch value={search} onChange={setSearch} onFilteringChange={setIsSearchFiltering} />
                </Box>
                {isLoading && (
                  <SelectionLoadingSurface
                    label={
                      selectedClock === "all"
                        ? `${activeCategory?.label ?? "all"} games`
                        : clockSignatureLabel(selectedClock)
                    }
                    progress={streamProgress}
                  />
                )}
              </Box>
            )}
            {tableClock != null && !isLoading && (
              <Box sx={{ mt: 1.5, flex: 1, minHeight: 0, overflow: "hidden" }}>
                {tableGroups.length === 0 ? (
                  <Box
                    sx={{
                      py: 5,
                      textAlign: "center",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {search.trim() ? <>No games match &quot;{search}&quot;.</> : "No games match these filters."}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr)",
                      gap: 3,
                      alignItems: "stretch",
                      height: "100%",
                      minHeight: 0,
                    }}
                  >
                    {tableGroups.map(({ timeControl, label, games: groupGames }) => (
                      <TimeControlTable
                        key={timeControl}
                        label={label}
                        games={groupGames}
                        onAnalyze={openAnalysisModal}
                        analysisAction={analysisAction}
                        sort={sort}
                        onSort={handleSort}
                        onLoadMore={() => void loadMoreGames()}
                        hasMoreGames={hasMoreGames}
                        isLoadingMore={isLoadingMore}
                        isVisuallyMounted
                        animateRows={tableGroups.length === 1}
                        isSearchFiltering={isSearchFiltering || isSortTransitioning}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </>
        </SelectionFlow>
      )}

      <Box component="footer" sx={{ display: "flex", justifyContent: "flex-end", flexShrink: 0, mt: "auto" }}>
        <Button
          onClick={() => setGameFilter(gameFilter === "all" ? "analyzed" : "all")}
          aria-label="Change game filter"
          aria-pressed={gameFilter === "analyzed"}
        >
          {gameFilter === "analyzed" ? "Showing analyzed games" : "Showing all games"}
        </Button>
      </Box>

      <GamesAnalysisModal
        ref={analysisModalRef}
        onComplete={(completion) => {
          finishGameAnalysis(completion, gamesOrigin(location), markGameAnalyzed, navigate);
        }}
      />
    </Box>
  );
};
